import { useEffect, useState, useCallback } from 'react';
import { Loader2, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useSocket } from '@/hooks/useSocket';
import { api } from '@/lib/api';
import type { RunningTask } from '@tracearr/shared';
import { formatDistanceToNow } from 'date-fns';

function TaskIcon({ status }: { status: RunningTask['status'] }) {
  switch (status) {
    case 'running':
      return <Loader2 className="text-primary h-4 w-4 animate-spin" />;
    case 'pending':
      return <Clock className="text-muted-foreground h-4 w-4" />;
    case 'complete':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'error':
      return <AlertCircle className="text-destructive h-4 w-4" />;
    default:
      return <Clock className="text-muted-foreground h-4 w-4" />;
  }
}

function TaskItem({ task }: { task: RunningTask }) {
  const startedAt = new Date(task.startedAt);
  const timeAgo = formatDistanceToNow(startedAt, { addSuffix: true });

  return (
    <div className="space-y-2 px-2 py-3">
      <div className="flex items-start gap-3">
        <TaskIcon status={task.status} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-medium">{task.name}</span>
            <Badge
              variant={task.status === 'running' ? 'default' : 'secondary'}
              className="shrink-0 text-xs"
            >
              {task.status === 'running' ? 'Running' : 'Queued'}
            </Badge>
          </div>
          {task.context && <p className="text-muted-foreground truncate text-xs">{task.context}</p>}
          <p className="text-muted-foreground text-xs">{task.message}</p>
          {task.progress !== null && task.status === 'running' && (
            <Progress value={task.progress} className="h-1.5" />
          )}
          <p className="text-muted-foreground/70 text-xs">Started {timeAgo}</p>
        </div>
      </div>
    </div>
  );
}

export function RunningTasksDropdown() {
  const { socket } = useSocket();
  const [tasks, setTasks] = useState<RunningTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch initial tasks
  const fetchTasks = useCallback(async () => {
    try {
      const response = await api.tasks.getRunning();
      setTasks(response.tasks);
    } catch (error) {
      console.error('Failed to fetch running tasks:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks]);

  // Subscribe to WebSocket updates
  useEffect(() => {
    if (!socket) return;

    const handleTasksUpdated = (updatedTasks: RunningTask[]) => {
      setTasks(updatedTasks);
    };

    socket.on('tasks:updated', handleTasksUpdated);
    return () => {
      socket.off('tasks:updated', handleTasksUpdated);
    };
  }, [socket]);

  // Poll for updates every 10 seconds as fallback
  useEffect(() => {
    const interval = setInterval(() => {
      void fetchTasks();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchTasks]);

  // Filter to show only active tasks (running or pending)
  const activeTasks = tasks.filter((t) => t.status === 'running' || t.status === 'pending');
  const runningTasks = activeTasks.filter((t) => t.status === 'running');
  const queuedTasks = activeTasks.filter((t) => t.status === 'pending');

  // Don't render anything if no active tasks
  if (isLoading || activeTasks.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Loader2 className="h-4 w-4 animate-spin" />
          {activeTasks.length > 0 && (
            <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-medium">
              {activeTasks.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Running Tasks
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {runningTasks.length > 0 && (
          <>
            {runningTasks.map((task) => (
              <TaskItem key={task.id} task={task} />
            ))}
          </>
        )}

        {queuedTasks.length > 0 && (
          <>
            {runningTasks.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
              Queued
            </DropdownMenuLabel>
            {queuedTasks.map((task) => (
              <TaskItem key={task.id} task={task} />
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
